import React, { useEffect, useState } from 'react';
import Dropzone from 'dropzone';
import axios from 'axios';
import { toAbsoluteUrl } from '@/utils/Assets';
import { LoaderType, Loading } from '@/layouts/hettwer/loading';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { useStatistic } from '@/contexts/StatisticContext.tsx';

interface Entity {
  p_id: string;
  name: string;
}

type EntityType = 'offer' | 'new_offer' | 'existing_offer';
type Mode = 'create' | 'addSource';

interface UploadDocumentProps {
  parentEntity?: Entity | null;
  objectEntity?: Entity | null;
  entityType: EntityType;
  mode: Mode;
  onSuccess?: () => void;
}

const UploadDocument: React.FC<UploadDocumentProps> = ({
  parentEntity = null,
  objectEntity = null,
  entityType,
  mode,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refreshStatistic } = useStatistic();

  const API_BASE = import.meta.env.VITE_APP_API_URL;

  const apiUrlMap: Record<EntityType, Record<Mode, string>> = {
    offer: {
      create: `${API_BASE}/offer/create/file`,
      addSource: `${API_BASE}/offer/add-source/file`
    },
    new_offer: {
      create: `${API_BASE}/new-offer/create/file`,
      addSource: `${API_BASE}/new-offer/add-source/file`
    },
    existing_offer: {
      create: `${API_BASE}/existing-offer/create/file`,
      addSource: `${API_BASE}/existing-offer/add-source/file`
    }
  };

  const API_FILE_UPLOAD = apiUrlMap[entityType][mode];

  // Validierung
  useEffect(() => {
    if (mode === 'create' && entityType === 'offer' && !parentEntity?.p_id) {
      enqueueSnackbar('Bitte wählen Sie zuerst das übergeordnete Angebot aus.', {
        variant: 'solid',
        state: 'danger'
      });
    }

    if (mode === 'addSource' && !objectEntity?.p_id) {
      enqueueSnackbar('Bitte wählen Sie das Zielobjekt aus.', {
        variant: 'solid',
        state: 'danger'
      });
    }
  }, [parentEntity, objectEntity, entityType, mode]);

  // Upload-URL bauen
  let uploadUrl = API_FILE_UPLOAD;

  if (mode === 'create' && parentEntity?.p_id && entityType !== 'offer') {
    uploadUrl = `${API_FILE_UPLOAD}/${parentEntity.p_id}`;
  }

  if (mode === 'addSource' && objectEntity?.p_id) {
    uploadUrl = `${API_FILE_UPLOAD}/${objectEntity.p_id}`;
  }

  useEffect(() => {
    Dropzone.autoDiscover = false;
    let dropzone: Dropzone | null = null;

    if (!loading) {
      dropzone = new Dropzone('#dropzone', {
        url: uploadUrl,
        maxFilesize: 5,
        maxFiles: 1,
        acceptedFiles: '.pdf,.doc,.docx,.txt',
        addRemoveLinks: true,
        dictDefaultMessage: `
          <div class="flex flex-col items-center">
            <div class="flex flex-row space-x-4 mb-2">
              <img src="${toAbsoluteUrl('/media/file-types/pdf.svg')}" alt="PDF" class="w-10 h-10"/>
              <img src="${toAbsoluteUrl('/media/file-types/doc.svg')}" alt="DOC" class="w-10 h-10"/>
              <img src="${toAbsoluteUrl('/media/file-types/txt.svg')}" alt="TXT" class="w-10 h-10"/>
            </div>
            <p><strong>Bestehendes Angebot hochladen</strong></p>
            <p>Ziehen Sie ein Angebotsdokument hierher oder klicken Sie zum Auswählen.</p>
            <p>Die Inhalte werden analysiert und als Trainingsdaten verwendet.</p>
            <p>Maximale Dateigröße: 5 MB.</p>
          </div>
        `,
        autoProcessQueue: false,
        init: function () {
          this.uploadFile = function () {};
        }
      });

      dropzone.on('addedfile', async (file) => {
        const authToken = localStorage.getItem('authToken');
        setLoading(true);

        try {
          const formData = new FormData();
          formData.append('file', file);

          const response = await axios.post(uploadUrl, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${authToken}`
            }
          });

          const newPath = `/offer/${response.data.p_id}`;

          if (window.location.pathname === newPath) {
            onSuccess?.();
          } else {
            navigate(newPath);
          }

          dropzone?.removeFile(file);
          await refreshStatistic();

          const successMessages: Record<EntityType, string> = {
            offer:
              mode === 'create'
                ? 'Das Angebot wurde erfolgreich erstellt.'
                : 'Die Quelle wurde erfolgreich zum Angebot hinzugefügt.',
            existing_offer:
              mode === 'create'
                ? 'Das bestehende Angebot wurde erfolgreich hochgeladen.'
                : 'Die Quelle wurde erfolgreich zum bestehenden Angebot hinzugefügt.',
            new_offer:
              mode === 'create'
                ? 'Das neue Angebot wurde erfolgreich erstellt.'
                : 'Die Quelle wurde erfolgreich zum neuen Angebot hinzugefügt.'
          };

          enqueueSnackbar(successMessages[entityType], {
            variant: 'solid',
            state: 'success'
          });
        } catch (err) {
          enqueueSnackbar('Das Dokument konnte nicht verarbeitet werden.', {
            variant: 'solid',
            state: 'danger'
          });
          dropzone?.removeFile(file);
        } finally {
          setLoading(false);
        }
      });
    }

    return () => {
      dropzone?.destroy();
    };
  }, [
    uploadUrl,
    loading,
    parentEntity,
    objectEntity,
    entityType,
    mode,
    onSuccess,
    navigate,
    refreshStatistic
  ]);

  return (
    <div className="card pb-2.5">
      <div className="card-header" id="upload_document">
        <h3 className="card-title">Bestehendes Angebot hochladen</h3>
      </div>

      <div className="card-body grid gap-5">
        {!loading && (
          <form
            action={uploadUrl}
            id="dropzone"
            className="dropzone !border-1 !border-dashed !border-gray-400 !bg-gray-50 !p-5 !text-center !font-medium !text-[0.8125rem] !text-gray-700"
          />
        )}

        {loading && (
          <Loading
            loadingText="Das Dokument wird analysiert und verarbeitet …"
            loaderType={LoaderType.LOADING_FILES}
            loading={loading}
            delay={0}
            sizePercentage={130}
          />
        )}
      </div>
    </div>
  );
};

export { UploadDocument };
